"use client";

import { useEffect, useState, useCallback } from "react";
import DematAccount from "./DematAccount";
import { selectLeadStatus, useAppSelector } from "@repo/web-store";
import { ICompany } from "../News/components/CompanyTag";
import {
  trackFloatingFabMasterCtaEvent,
  trackTickerStripDisplayBannerButtonClick,
  trackTickerStripDisplayBannerIconClick,
} from "./unifiedCt";
import Image from "next/image";


interface OpenDematFlowBannerProps {
  openDematButtonText: string;
  resumeJourneyText?: string;
  showUnifiedCt?: boolean;
  compName: string;
  exchId: string;
  formattedSymbol: string;
  nseToken: number;
  bseToken: number;
  showTradeNow?: boolean;
  tradeNowText?: string;
  showBuySellButtons?: boolean;
  buyText?: string;
  low_brokerage_img: string;
  sellText?: string;
  brokerageLabelText?: string;
  brokerageMainText?: string;
  floatingBannerMainText?: string;
  floatingBannerSubText?: string;
  showFloatingBannerOnDesktop?: boolean;
  showFloatingBannerOnMobile?: boolean;
  showFloatingBannerOnScroll?: boolean;
  headerName?: string;
  companies?: ICompany[];
  isPdp?: boolean;
  modalTitle?: string;
  loadOnPageLoad?: boolean;
}

const OpenDematFloatBanner: React.FC<OpenDematFlowBannerProps> = ({
  openDematButtonText,
  resumeJourneyText,
  showUnifiedCt,
  compName,
  exchId,
  formattedSymbol,
  nseToken,
  bseToken,
  showTradeNow,
  tradeNowText,
  showBuySellButtons,
  buyText,
  low_brokerage_img,
  sellText,
  brokerageLabelText,
  brokerageMainText,
  floatingBannerMainText,
  floatingBannerSubText,
  showFloatingBannerOnDesktop,
  showFloatingBannerOnMobile,
  showFloatingBannerOnScroll,
  headerName,
  companies,
  isPdp = false,
  modalTitle,
  loadOnPageLoad = false,
}) => {
  const [showBanner, setShowBanner] = useState(loadOnPageLoad);
  const [manuallyClosed, setManuallyClosed] = useState(false);
  const leadStatus = useAppSelector(selectLeadStatus);

  const trackTickerStripClick = useCallback(
    (ctaText: string) => {
      void trackTickerStripDisplayBannerButtonClick(
        floatingBannerMainText,
        ctaText,
      );
    },
    [floatingBannerMainText],
  );

  const trackTickerStripClose = useCallback(() => {
    void trackTickerStripDisplayBannerIconClick(
      floatingBannerMainText,
      brokerageMainText ?? "BROKERAGE",
    );
  }, [brokerageMainText, floatingBannerMainText]);

  const trackFloatingFabMasterCta = useCallback(
    (ctaText: string, submissionStatus: string = "") => {
      void trackFloatingFabMasterCtaEvent(
        headerName ?? "Bajaj Broking",
        ctaText,
        submissionStatus,
      );
    },
    [headerName],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (loadOnPageLoad) return;

    let ticking = false;
    const updateBannerVisibility = () => {
      if (manuallyClosed) return;

      const shouldShow = window.scrollY > 100;
      setShowBanner((prev) => {
        if (prev !== shouldShow) return shouldShow;
        return prev;
      });

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateBannerVisibility);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    updateBannerVisibility();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [manuallyClosed, loadOnPageLoad]);

  const handleClose = () => {
    setShowBanner(false);
    setManuallyClosed(true);
    trackTickerStripClose();
  };

  if (!showBanner) return null;

  return (
    <>
      <div
        className={`fixed bottom-0 left-0 z-[99999] hidden min-h-[73px] w-screen shadow-[-4px_-4px_6px_#00000014] md:flex ${
          leadStatus === "ETB" && isPdp === true ? "md:hidden" : ""
        }`}
        aria-live="polite"
      >
        <div className="w-[235px] shrink-0 bg-[#eff2ff]">
          <img
            src={low_brokerage_img}
            alt="Low brokerage by Bajaj Broking"
            width={235}
            height={73}
            loading="eager"
            fetchPriority="high"
            className="h-full w-full object-cover"
            draggable={false}
            style={{ background: "#f3f6fc", borderRadius: "4px" }}
          />
    
        </div>

        <div className="flex flex-shrink flex-grow items-center justify-between bg-white px-6 py-4">
          <div className="text-container fw-500">
            <div className="text-base font-[500] text-black">
              {floatingBannerMainText ??
                "Start your trading journey with Bajaj Broking"}
            </div>
            <div className="text-xs font-[500] text-gray-600">
              {floatingBannerSubText ??
                "Trade easily in stocks, futures & options and IPOs"}
            </div>
          </div>
          <DematAccount
            openDematButtonText={openDematButtonText ?? "Open Demat Account"}
            resumeJourneyText={resumeJourneyText}
            showUnifiedCt={showUnifiedCt ?? true}
            compName={compName}
            exchId={exchId}
            formattedSymbol={formattedSymbol}
            nseToken={nseToken}
            bseToken={bseToken}
            showTradeNow={showTradeNow}
            tradeNowText={tradeNowText}
            showBuySellButtons={showBuySellButtons}
            buyText={buyText}
            sellText={sellText}
            headerName={headerName}
            companies={companies}
            modalTitle={modalTitle}
            onMasterCtaClick={trackTickerStripClick}
          />
        </div>

        <button
          className="bg-lgray-200 flex min-h-[73px] w-[73px] cursor-pointer items-center justify-center"
          onClick={handleClose}
          aria-label="Close banner"
        >
          <i className="icomoon text-[20px] before:content-['\e914']"></i>
        </button>
      </div>
      <div className="z-9 fixed bottom-[80px] right-[20px] block md:hidden">
        <DematAccount
          openDematButtonText={openDematButtonText ?? "Open Demat Account"}
          resumeJourneyText={resumeJourneyText}
          showUnifiedCt={showUnifiedCt ?? true}
          compName={compName}
          exchId={exchId}
          formattedSymbol={formattedSymbol}
          nseToken={nseToken}
          bseToken={bseToken}
          isFloating={true}
          showTradeNow={showTradeNow}
          tradeNowText={tradeNowText}
          showBuySellButtons={showBuySellButtons}
          buyText={buyText}
          sellText={sellText}
          headerName={headerName}
          companies={companies}
          modalTitle={modalTitle}
          onMasterCtaClick={trackFloatingFabMasterCta}
        />
      </div>
    </>
  );
};

export default OpenDematFloatBanner;
